import type { PulseConnectionResponse } from '@orion/contracts';
import { Button } from '@orion/ui';
import { ApiError } from '../../../lib/api-client.js';
import { useToast } from '../../../lib/toast.js';
import { PROVIDER_LABEL, PulseConnectionCard, STATUS_LABEL } from './PulseConnectionCard.js';
import { useAdminDisconnectPulse, useRemindPulseOwner } from './api.js';

/** The read-only summary shown for a connection that isn't the viewer's own
 * — reachable only from the "All connections" admin table. Unlike
 * `PulseConnectionCard` (which this same drawer renders unmodified for the
 * viewer's own connections), there's no settings editing or Sync now here:
 * those are the owner's own actions. The one thing an admin genuinely can
 * do for someone else's broken mailbox is nudge them — Google/Microsoft's
 * OAuth consent has to come from the owner's own sign-in, not an admin
 * acting on their behalf. */
function OtherOwnersConnection({ connection, onClose }: { connection: PulseConnectionResponse; onClose: () => void }) {
  const remind = useRemindPulseOwner();
  const adminDisconnect = useAdminDisconnectPulse();
  const toast = useToast();
  const status = STATUS_LABEL[connection.status];
  const ownerFirstName = connection.ownerDisplayName.split(' ')[0];

  const sendReminder = async () => {
    try {
      await remind.mutateAsync(connection.id);
      toast.success(`Reminder sent to ${connection.ownerDisplayName}.`);
    } catch (err) {
      toast.error(err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Failed to send reminder.');
    }
  };

  const disconnect = async () => {
    try {
      await adminDisconnect.mutateAsync(connection.id);
      toast.success('Disconnected.');
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Failed to disconnect.');
    }
  };

  return (
    <div className="flex flex-col gap-4 rounded-dialog border border-border bg-surface p-5 shadow-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <span className="text-[15px] font-semibold">{connection.emailAddress}</span>
          <span className="text-xs text-text-muted">
            Owner: {connection.ownerDisplayName} · {PROVIDER_LABEL[connection.provider]}
          </span>
        </div>
        <span className={`flex h-6 shrink-0 items-center rounded-full px-2.5 text-xs font-semibold ${status.className}`}>{status.label}</span>
      </div>

      {connection.status === 'needs_reconnect' && (
        <div role="alert" className="flex flex-col gap-2.5 rounded-control bg-amber-soft px-3 py-2 text-sm text-amber-text">
          <span>This mailbox needs to be reconnected — access may have expired or been revoked.</span>
          <Button size="sm" variant="secondary" className="self-start" loading={remind.isPending} onClick={sendReminder}>
            Remind {ownerFirstName} to reconnect
          </Button>
          <span className="text-xs opacity-80">Only {ownerFirstName} can complete this — Google/Microsoft requires their own sign-in, not yours.</span>
        </div>
      )}

      <div className="flex items-center gap-4 text-[13px] text-text-secondary">
        <span>Last sync: {connection.lastSyncAt ? new Date(connection.lastSyncAt).toLocaleString('en-US') : 'Never yet'}</span>
      </div>
      {connection.lastSyncError && <p className="text-xs text-danger">Last error: {connection.lastSyncError}</p>}

      {connection.stats && (
        <div className="flex items-center gap-5 border-t border-divider pt-3.5 text-[13px]">
          <span>
            <span className="font-semibold text-ink">{connection.stats.processed}</span> <span className="text-text-muted">messages processed</span>
          </span>
          <span>
            <span className="font-semibold text-success">{connection.stats.leadsCreated}</span> <span className="text-text-muted">leads created</span>
          </span>
        </div>
      )}

      <div className="flex flex-col gap-1.5 border-t border-divider pt-3.5">
        <Button size="sm" variant="secondary" className="self-start" loading={adminDisconnect.isPending} onClick={disconnect}>
          Disconnect (admin override)
        </Button>
        <span className="text-xs text-text-faint">Settings and Sync now are {ownerFirstName}&apos;s own actions — not available here.</span>
      </div>
    </div>
  );
}

/** The "All connections" table's row-click target. For the viewer's own
 * connection this is just `PulseConnectionCard` — the exact same card the
 * "My mailbox" tab shows, so an admin who happens to own one of the rows
 * they're browsing gets full settings/Sync now/Disconnect, not a second,
 * read-only implementation of the same thing. */
export function PulseConnectionDrawer({ connection, isMine, onClose }: { connection: PulseConnectionResponse; isMine: boolean; onClose: () => void }) {
  return (
    <>
      <div role="presentation" onClick={onClose} className="fixed inset-0 z-40 bg-ink/30" />
      <div className="fixed inset-y-0 right-0 z-50 flex w-110 max-w-[90vw] flex-col gap-3 overflow-y-auto bg-ground p-4 shadow-dialog">
        <button type="button" onClick={onClose} aria-label="Close" className="self-end text-2xl leading-none text-text-faint hover:text-text-muted">
          &times;
        </button>
        {isMine ? <PulseConnectionCard connection={connection} /> : <OtherOwnersConnection connection={connection} onClose={onClose} />}
      </div>
    </>
  );
}
